import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { required, useGetIdentity, useGetOne, useTranslate } from "ra-core";
import { DateTimeInput } from "@/components/admin";

import { useIsCampaignAdmin } from "../campaigns/useIsCampaignAdmin";
import { contactOptionText } from "../misc/ContactOption";
import { translateConfigChoices } from "../misc/getTranslatedConfigLabel";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Sale } from "../types";
import { getDefaultTaskAssigneeId } from "./getDefaultTaskAssigneeId";
import { RecurringTaskFields } from "./RecurringTaskFields";

const saleOptionRenderer = (choice: Sale) =>
  `${choice.first_name} ${choice.last_name}`;

const TaskAssigneeOnContactChange = () => {
  const contactId = useWatch({ name: "contact_id" });
  const { identity } = useGetIdentity();
  const canAssignOthers = useIsCampaignAdmin();
  const { setValue } = useFormContext();
  const { data: contact } = useGetOne(
    "contacts",
    { id: contactId! },
    { enabled: !!contactId && canAssignOthers },
  );
  const { data: company } = useGetOne(
    "companies",
    { id: contact?.company_id! },
    { enabled: !!contact?.company_id && canAssignOthers },
  );

  useEffect(() => {
    if (!canAssignOthers || !identity || !contactId) {
      return;
    }

    setValue(
      "sales_id",
      getDefaultTaskAssigneeId({
        contact,
        company,
        identityId: identity.id,
        canAssignOthers: true,
      }),
    );
  }, [canAssignOthers, company, contact, contactId, identity, setValue]);

  return null;
};

export const TaskFormContent = ({
  selectContact,
  includeRecurrence = false,
  allowAssigneeSelect = false,
}: {
  selectContact?: boolean;
  includeRecurrence?: boolean;
  allowAssigneeSelect?: boolean;
}) => {
  const { taskTypes } = useConfigurationContext();
  const translate = useTranslate();
  const translatedTaskTypes = translateConfigChoices(
    taskTypes,
    "taskTypes",
    translate,
  );
  return (
    <div className="flex flex-col gap-4">
      <TextInput
        autoFocus
        source="text"
        validate={required()}
        multiline
        className="m-0"
        helperText={false}
      />
      {selectContact && (
        <ReferenceInput source="contact_id" reference="contacts_summary">
          <AutocompleteInput
            label="resources.tasks.fields.contact_id"
            optionText={contactOptionText}
            helperText={false}
            validate={required()}
            modal
          />
        </ReferenceInput>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DateTimeInput
          source="due_date"
          helperText={false}
          validate={required()}
        />
        <SelectInput
          source="type"
          validate={required()}
          choices={translatedTaskTypes}
          optionText="label"
          optionValue="value"
          defaultValue="none"
          helperText={false}
        />
      </div>
      {allowAssigneeSelect && (
        <>
          {selectContact && <TaskAssigneeOnContactChange />}
          <ReferenceInput
            source="sales_id"
            reference="sales"
            filter={{
              "disabled@neq": true,
            }}
          >
            <AutocompleteInput
              label="resources.tasks.fields.sales_id"
              optionText={saleOptionRenderer}
              helperText={false}
              validate={required()}
              modal
            />
          </ReferenceInput>
        </>
      )}
      {includeRecurrence && <RecurringTaskFields />}
    </div>
  );
};

export const TaskEditFormContent = () => {
  const isCampaignAdmin = useIsCampaignAdmin();
  return <TaskFormContent allowAssigneeSelect={isCampaignAdmin} />;
};
